import { proxy, subscribe } from 'valtio';
import { watch } from 'valtio/utils';
import type { LiveServicesNumberMapping } from '@api/generated/graphql';
import ServiceQueue from '../types/ServiceQueue';
import ServiceSession from '../types/ServiceSession';
import ServiceUser from '../types/ServiceUser';
import { valtioDevTools } from '@lib/utils/devTools';
import { ClientServerTimeDiff } from './clientServerTimeDiff/types';

export type LiveServicesMapping = Pick<
  LiveServicesNumberMapping,
  'phoneNumber' | 'tenantId'
>;

/** localStorage key for persisting time sync data */
const TIME_SYNC_STORAGE_KEY = 'client_server_time_diff';

export type ServiceState = {
  version: number;
  loading: boolean;
  loadingStatus: string;
  error: unknown;
  id: string | null;
  contactCenterId: string | null;
  sessions: Record<string, ServiceSession>;
  queues: ServiceQueue[];
  queueById: Record<string, ServiceQueue>;
  users: ServiceUser[];
  userById: Record<string, ServiceUser>;
  userByContactCenterUserId: Record<string, ServiceUser>;
  serviceConnected: boolean;
  contactCenterUserId: string | null;
  tenantIds: string[];
  isLiveServicesNetwork: boolean;
  liveServicesMappings: LiveServicesMapping[];
  clientServerTimeDiff: ClientServerTimeDiff | null;

  updateUserStatusById: Record<
    string,
    {
      loading: boolean;
      error?: string;
    } | null
  >;
};

export const initState: ServiceState = {
  version: 0,
  loading: true, // Setting to true to avoid race condition, app should load only after workspace is initialized
  loadingStatus: '',
  error: null,
  id: null,
  contactCenterId: null,
  users: [],
  userById: {},
  userByContactCenterUserId: {},
  sessions: {},
  queues: [],
  queueById: {},
  serviceConnected: false,
  contactCenterUserId: null,
  tenantIds: [],
  isLiveServicesNetwork: false,
  liveServicesMappings: [],
  updateUserStatusById: {},
  clientServerTimeDiff: JSON.parse(
    localStorage.getItem(TIME_SYNC_STORAGE_KEY) ?? 'null',
  ),
};

export const serviceState = proxy<ServiceState>(initState);

subscribe(
  serviceState,
  (ops) => {
    for (const [operation, paths] of ops) {
      // only rebuild the maps if the users/queues object is set
      // this assumes that the users/queues object is always set as a whole (current behavior)
      // if this changes, we need to revisit this.
      if (operation === 'set' && paths[0] === 'users' && paths.length === 1) {
        const userById: ServiceState['userById'] = {};
        const userByContactCenterUserId: ServiceState['userByContactCenterUserId'] =
          {};
        serviceState.users.forEach((u) => {
          userById[u.id] = u;
          if (u.contactCenterUserId) {
            userByContactCenterUserId[u.contactCenterUserId] = u;
          }
        });
        serviceState.userById = userById;
        serviceState.userByContactCenterUserId = userByContactCenterUserId;
      } else if (
        operation === 'set' &&
        paths[0] === 'queues' &&
        paths.length === 1
      ) {
        const queueById: ServiceState['queueById'] = {};
        serviceState.queues.forEach((q) => {
          queueById[q.id] = q;
        });
        serviceState.queueById = queueById;
      }
    }
  },
  true,
);

// Automatically sync clientServerTimeDiff to localStorage when it changes
watch((get) => {
  const clientServerTimeDiff = get(serviceState).clientServerTimeDiff;
  try {
    if (clientServerTimeDiff) {
      localStorage.setItem(
        TIME_SYNC_STORAGE_KEY,
        JSON.stringify(clientServerTimeDiff),
      );
    } else {
      localStorage.removeItem(TIME_SYNC_STORAGE_KEY);
    }
  } catch (error) {
    console.error(
      'Failed to sync clientServerTimeDiff to localStorage:',
      error,
    );
  }
});

valtioDevTools(serviceState, 'serviceState');
